import type { DriveStep } from 'driver.js';

// Step index where we navigate to settings page (0-indexed)
export const WELCOME_TOUR_SETTINGS_NAV_INDEX = 13;

// Step index where we navigate back to dashboard (0-indexed)
export const WELCOME_TOUR_DASHBOARD_NAV_INDEX = 21;

export const welcomeTourSteps: DriveStep[] = [
  // === DASHBOARD SECTION ===
  {
    popover: {
      title: 'Welcome to Auto-Translator',
      description: 'This platform helps you translate documents from Salesforce using AI. Let\'s take a quick tour of the main features.',
      side: 'over',
      align: 'center',
    },
  },
  {
    element: '[data-onboarding="sidebar"]',
    popover: {
      title: 'Sidebar Navigation',
      description: 'The sidebar is your navigation hub. It shows the different pipeline sections, each with a badge showing how many cases are in that stage.',
      side: 'right',
      align: 'start',
    },
  },
  // Pipeline Overview - zoom into section headers
  {
    element: '[data-onboarding="pipeline-new-card"]',
    popover: {
      title: 'New Cases',
      description: 'Cases from Salesforce that haven\'t been downloaded yet. This is where the pipeline starts.',
      side: 'bottom',
      align: 'center',
    },
  },
  {
    element: '[data-onboarding="pipeline-queue-card"]',
    popover: {
      title: 'Translation Queue',
      description: 'Cases being processed: downloaded, translating, or ready for review. Most of your work happens here.',
      side: 'bottom',
      align: 'center',
    },
  },
  {
    element: '[data-onboarding="pipeline-approved-card"]',
    popover: {
      title: 'Approved',
      description: 'Cases you\'ve reviewed and approved. These are ready to upload back to Salesforce.',
      side: 'bottom',
      align: 'center',
    },
  },
  // Quick Stats - zoom into each stat
  {
    element: '[data-onboarding="stat-today"]',
    popover: {
      title: 'Today\'s Progress',
      description: 'Number of cases completed today. Track your daily productivity.',
      side: 'bottom',
      align: 'center',
    },
  },
  {
    element: '[data-onboarding="stat-week"]',
    popover: {
      title: 'Weekly Progress',
      description: 'Cases completed this week. See your cumulative progress.',
      side: 'bottom',
      align: 'center',
    },
  },
  {
    element: '[data-onboarding="stat-processing"]',
    popover: {
      title: 'Currently Processing',
      description: 'Cases actively being translated right now. The spinner shows when work is in progress.',
      side: 'bottom',
      align: 'center',
    },
  },
  {
    element: '[data-onboarding="stat-errors"]',
    popover: {
      title: 'Errors',
      description: 'Cases that failed and need attention. Check the Issues section for details.',
      side: 'bottom',
      align: 'center',
    },
  },
  // Case table with search
  {
    element: '[data-onboarding="search-input"]',
    popover: {
      title: 'Search Cases',
      description: 'Type a case number to quickly find specific cases. Search filters the current section in real-time.',
      side: 'bottom',
      align: 'start',
    },
  },
  {
    element: '[data-onboarding="case-table"]',
    popover: {
      title: 'Case List',
      description: 'Your cases are listed here. Click any row to open the review page and see the translation.',
      side: 'top',
      align: 'center',
    },
  },
  {
    element: '[data-onboarding="theme-toggle"]',
    popover: {
      title: 'Dark/Light Mode',
      description: 'Toggle between light and dark themes for comfortable viewing.',
      side: 'right',
      align: 'center',
    },
  },
  {
    element: '[data-onboarding="settings-link"]',
    popover: {
      title: 'Settings',
      description: 'Let\'s explore the Settings page to see how you configure AI providers and prompts. Click Next to continue.',
      side: 'right',
      align: 'center',
    },
  },
  // === SETTINGS SECTION (step index 13) - Navigation happens before this step ===
  {
    popover: {
      title: 'Settings Page',
      description: 'This is the Settings page where you configure your AI translation settings. Let\'s walk through each option.',
      side: 'over',
      align: 'center',
    },
  },
  // LLM Provider Section
  {
    element: '[data-onboarding="provider-gemini"]',
    popover: {
      title: 'AI Provider Selection',
      description: 'Choose your AI provider here. Click on any provider button to select it for configuration.',
      side: 'bottom',
      align: 'center',
    },
  },
  // Model configuration fields
  {
    element: '[data-onboarding="model-input"]',
    popover: {
      title: 'Model Name',
      description: 'The specific model version to use. Examples:\n- gemini-2.5-flash\n- gpt-4o\n- claude-3-5-sonnet-20241022\n\nClick Edit to change this.',
      side: 'bottom',
      align: 'start',
    },
  },
  {
    element: '[data-onboarding="temperature-slider"]',
    popover: {
      title: 'Temperature',
      description: 'Controls creativity vs consistency:\n\n- 0.0-0.3: Very literal, consistent\n- 0.4-0.7: Balanced\n- 0.8+: More creative\n\nRecommended: 0.1 for translations',
      side: 'bottom',
      align: 'center',
    },
  },
  // Prompt Section
  {
    element: '[data-onboarding="prompt-dropdown"]',
    popover: {
      title: 'Prompt Selector',
      description: 'Choose which prompt to edit. The star indicates the currently active prompt used for translations.',
      side: 'bottom',
      align: 'start',
    },
  },
  {
    element: '[data-onboarding="system-prompt-field"]',
    popover: {
      title: 'System Prompt',
      description: 'Defines the AI\'s identity and rules. This is like a "constitution" - it sets the tone, expertise level, and constraints for all translations.\n\nClick on the header to expand and view the full content.',
      side: 'top',
      align: 'center',
    },
  },
  {
    element: '[data-onboarding="user-prompt-field"]',
    popover: {
      title: 'User Prompt',
      description: 'Specific task instructions. Tell the AI exactly what to do with each document - formatting, special handling, output requirements.\n\nClick on the header to expand and view the full content.',
      side: 'top',
      align: 'center',
    },
  },
  {
    element: '[data-onboarding="new-prompt-btn"]',
    popover: {
      title: 'Create New Prompt',
      description: 'Create a new prompt template. Useful for different translation styles or document types.',
      side: 'left',
      align: 'center',
    },
  },
  // === BACK TO DASHBOARD (step index 21) - Navigation happens before this step ===
  {
    element: '[data-onboarding="help-trigger"]',
    popover: {
      title: 'Help & Tutorials',
      description: 'Click this button anytime to access tutorials for any feature. You can also find explanations of concepts like case statuses and the translation pipeline.',
      side: 'left',
      align: 'end',
    },
  },
  {
    popover: {
      title: 'You\'re Ready!',
      description: 'That\'s the basics! Start by downloading cases from "New in Salesforce", then translate them, review the output, and upload back to Salesforce. Click the help button if you need guidance on any specific feature.',
      side: 'over',
      align: 'center',
    },
  },
];
